import { resolveFileUrl } from '../../utils/fileUrl';

interface EmployeeData {
  name: string;
  job: string;
  nationalId: string;
  address: string;
}

export type LetterLanguage = 'ar' | 'en';

/** Optional letterhead/footer configured on a letter template. */
export interface LetterLayout {
  logoUrl?: string;
  logoPosition?: 'top' | 'right' | 'left';
  logoSize?: 'small' | 'medium' | 'large';
  headerHtml?: string;
  footerHtml?: string;
}

const DEFAULT_HEADER_HTML = `<div style="font-size: 18px; font-weight: bold;">PetroMarine Offshore Projects Support Co.</div>
      <div style="font-size: 16px; font-weight: bold; margin-top: 5px;">شركة بترومارين لدعم المشاريع البحرية</div>`;

const LOGO_HEIGHTS = { small: 50, medium: 80, large: 110 } as const;

/**
 * Letterhead for print. `defaultText` is the fallback heading when the
 * template has no custom headerHtml — pass '' for letters issued through
 * an agent (immigration) where the company name must not appear.
 */
export const buildLetterHeaderHtml = (layout?: LetterLayout, defaultText = DEFAULT_HEADER_HTML): string => {
  const logo = resolveFileUrl(layout?.logoUrl);
  const custom = layout?.headerHtml?.trim();
  const heading = custom || defaultText;
  const text = heading ? `<div style="text-align: center;">${heading}</div>` : '';
  if (!logo) {
    if (!heading) return '';
    return `<div class="letterhead" style="margin-bottom: 20px; border-bottom: 2px solid #000; padding-bottom: 10px;">${text}</div>`;
  }
  const h = LOGO_HEIGHTS[layout?.logoSize || 'medium'];
  const img = `<img src="${logo}" alt="Company logo" style="max-height: ${h}px; display: block;" />`;
  const pos = layout?.logoPosition || 'top';
  const inner = pos === 'top'
    ? `<div style="text-align: center;">${img.replace('display: block;', 'display: block; margin: 0 auto 8px;')}${heading}</div>`
    : `<div style="display: flex; direction: ltr; align-items: center; justify-content: ${pos === 'right' ? 'flex-end' : 'flex-start'}; gap: 15px;">${pos === 'right' ? `${text}${img}` : `${img}${text}`}</div>`;
  return `<div class="letterhead" style="margin-bottom: 20px; border-bottom: 2px solid #000; padding-bottom: 10px;">${inner}</div>`;
};

export const buildLetterFooterHtml = (layout?: LetterLayout, fixed = false): string => {
  const custom = layout?.footerHtml?.trim();
  if (!custom) return '';
  const style = fixed
    ? 'position: fixed; bottom: 0; left: 0; right: 0; border-top: 1px solid #000; padding-top: 8px; background: #fff;'
    : 'margin-top: 30px; border-top: 1px solid #000; padding-top: 8px;';
  return `<div class="letterfooter" style="${style}">${custom}</div>`;
};

interface LetterText {
  dir: 'rtl' | 'ltr';
  align: 'right' | 'left';
  dateAlign: 'right' | 'left';
  dateLabel: string;
  recipient: (branchName: string) => string;
  greeting: string;
  body: (branchName: string) => string;
  headers: { index: string; name: string; job: string; nationalId: string; address: string };
  closing: string;
  signature: string;
  title: string;
  formatDate: () => string;
}

const TEXTS: Record<LetterLanguage, LetterText> = {
  ar: {
    dir: 'rtl',
    align: 'right',
    dateAlign: 'left',
    dateLabel: 'تاريخ',
    recipient: (b) => `السيد/ قائد مكتب مخابرات حرس حدود – ${b}`,
    greeting: 'تحية طيبة وبعد ،،،',
    body: (b) => `يرجى من سيادتكم التكرم بعمل اللازم نحو استخراج تصاريح حرس الحدود للوحدة ${b} للافراد الاتي بياناتهم:`,
    headers: { index: 'م', name: 'الاسم', job: 'الوظيفة', nationalId: 'الرقم القومي', address: 'العنوان' },
    closing: 'ونحن إذ نشكر حسن تعاونكم المعهود معنا',
    signature: 'إدارة الموارد البشرية',
    title: 'تصاريح حرس الحدود - أفراد',
    formatDate: () => new Date().toLocaleDateString('ar-EG'),
  },
  en: {
    dir: 'ltr',
    align: 'left',
    dateAlign: 'right',
    dateLabel: 'Date',
    recipient: (b) => `To: Commander of Border Guard Intelligence Office – ${b}`,
    greeting: 'Dear Sir,',
    body: (b) => `Kindly take the necessary action to issue Border Guard permits for unit ${b} for the following personnel:`,
    headers: { index: '#', name: 'Name', job: 'Job Title', nationalId: 'National ID', address: 'Address' },
    closing: 'Thank you for your continued cooperation.',
    signature: 'Human Resources Department',
    title: 'Border Guard Permits - Personnel',
    formatDate: () => new Date().toLocaleDateString('en-GB'),
  },
};

/**
 * The personnel details table only — this is what the {{EMPLOYEE_DETAILS}}
 * placeholder stands for inside a template. Kept separate from the full
 * letter so a template's own recipient/greeting/closing text is not
 * duplicated around it.
 */
export const generatePersonnelTableHTML = (
  employeeData: EmployeeData[],
  language: LetterLanguage = 'ar'
): string => {
  const t = TEXTS[language];
  return `<table style="width: 100%; border-collapse: collapse; margin: 15px 0; direction: ${t.dir};" border="1" cellpadding="8" cellspacing="0">
        <thead>
          <tr style="background-color: #e0e0e0;">
            <th style="border: 1px solid #000; padding: 8px; text-align: center;">${t.headers.index}</th>
            <th style="border: 1px solid #000; padding: 8px; text-align: center;">${t.headers.name}</th>
            <th style="border: 1px solid #000; padding: 8px; text-align: center;">${t.headers.job}</th>
            <th style="border: 1px solid #000; padding: 8px; text-align: center;">${t.headers.nationalId}</th>
            <th style="border: 1px solid #000; padding: 8px; text-align: center;">${t.headers.address}</th>
          </tr>
        </thead>
        <tbody>
          ${employeeData.map((emp, index) => `
            <tr>
              <td style="border: 1px solid #000; padding: 8px; text-align: center;">${index + 1}</td>
              <td style="border: 1px solid #000; padding: 8px; text-align: center;">${emp.name}</td>
              <td style="border: 1px solid #000; padding: 8px; text-align: center;">${emp.job}</td>
              <td style="border: 1px solid #000; padding: 8px; text-align: center;">${emp.nationalId}</td>
              <td style="border: 1px solid #000; padding: 8px; text-align: center;">${emp.address}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>`;
};

export const generatePersonnelPreviewHTML = (
  employeeData: EmployeeData[],
  branchName: string,
  language: LetterLanguage = 'ar'
): string => {
  const t = TEXTS[language];

  return `<div dir="${t.dir}" style="font-family: 'Arial', 'Tahoma', sans-serif; text-align: ${t.align}; line-height: 2;">
      <p style="text-align: ${t.dateAlign}; margin-bottom: 20px;">${t.dateLabel}: ${t.formatDate()}</p>

      <p style="font-weight: bold; margin-bottom: 15px;">${t.recipient(branchName)}</p>

      <p style="margin-bottom: 5px;">${t.greeting}</p>

      <p style="margin-bottom: 15px;">${t.body(branchName)}</p>

      ${generatePersonnelTableHTML(employeeData, language)}

      <p style="margin-top: 20px;">${t.closing}</p>

      <p style="margin-top: 30px; text-align: ${t.dateAlign};">${t.signature}</p>
    </div>`;
};

export const buildPersonnelPrintHTML = (
  employeeData: EmployeeData[],
  branchName: string,
  language: LetterLanguage = 'ar',
  layout?: LetterLayout,
  bodyHtml?: string
): string => {
  const t = TEXTS[language];
  const hasFooter = !!layout?.footerHtml?.trim();
  const body = bodyHtml?.trim() || `
    <div class="date">${t.dateLabel}: ${t.formatDate()}</div>

    <p class="recipient">${t.recipient(branchName)}</p>

    <p>${t.greeting}</p>

    <p>${t.body(branchName)}</p>

    <table>
      <thead>
        <tr>
          <th>${t.headers.index}</th>
          <th>${t.headers.name}</th>
          <th>${t.headers.job}</th>
          <th>${t.headers.nationalId}</th>
          <th>${t.headers.address}</th>
        </tr>
      </thead>
      <tbody>
        ${employeeData.map((emp, index) => `
          <tr>
            <td>${index + 1}</td>
            <td>${emp.name}</td>
            <td>${emp.job}</td>
            <td>${emp.nationalId}</td>
            <td>${emp.address}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>

    <div class="footer">
      <p>${t.closing}</p>
    </div>

    <div class="signature">
      <p>${t.signature}</p>
    </div>`;

  return `<!DOCTYPE html>
<html dir="${t.dir}">
  <head>
    <meta charset="UTF-8">
    <title>${t.title}</title>
    <style>
      @page { margin: 20mm; size: A4; }
      body {
        font-family: 'Arial', 'Tahoma', sans-serif;
        font-size: 14px;
        line-height: 2;
        margin: 0;
        padding: 20px;
        ${hasFooter ? 'padding-bottom: 90px;' : ''}
        direction: ${t.dir};
        text-align: ${t.align};
      }
      .date { text-align: ${t.dateAlign}; margin-bottom: 20px; }
      .recipient { font-weight: bold; margin-bottom: 15px; }
      table {
        width: 100%;
        border-collapse: collapse;
        margin: 15px 0;
      }
      th, td {
        border: 1px solid #000;
        padding: 8px;
        text-align: center;
      }
      th { background-color: #e0e0e0; font-weight: bold; }
      .footer { margin-top: 30px; }
      .signature { text-align: ${t.dateAlign}; margin-top: 40px; }
      @media print {
        body { padding: 0; ${hasFooter ? 'padding-bottom: 90px;' : ''} }
      }
    </style>
  </head>
  <body>
    ${buildLetterHeaderHtml(layout)}
    ${body}
    ${buildLetterFooterHtml(layout, true)}
  </body>
</html>`;
};
